import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faRobot, faTimes, faThumbsDown, faThumbsUp } from '@/icons';
import { useAuthContext } from '../contexts/AuthContext';
import { useCalculonChatFabSuppression } from '../contexts/CalculonChatFabSuppressionContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { ProUpgradeModal } from './ProUpgradeModal';
import { CustomTooltip } from './CustomTooltip';
import {
    calculonChatBotApiService,
    CalculonChatBotMessage,
} from '../services/calculonChatBotApiService';
import { renderTextWithLinks } from '../utils/renderTextWithLinks';

const FEEDBACK_THANK_YOU =
    'Thank you for your feedback. Your concern has been logged and will be reviewed by the Calculist team';
const FEEDBACK_THANK_YOU_HELPFUL = 'Thank you for your feedback!';

const getSubscriptionType = (teamSubscriptionType?: string): string => {
    try {
        const override = sessionStorage.getItem('subscription_type_override');
        if (override) {
            return override;
        }
    } catch {
        // Ignore session storage failures and use team subscription fallback.
    }

    return teamSubscriptionType ?? '';
};

export const CalculonChatBotFAB: React.FC = () => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuthContext();
    const { suppress: suppressChatFab } = useCalculonChatFabSuppression();
    const calculonChatBotEnabled = useFeatureFlag('CalculonChatBot');

    const [isOpen, setIsOpen] = useState(false);
    const [showProUpgradeModal, setShowProUpgradeModal] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [input, setInput] = useState('');
    const messageListRef = useRef<HTMLDivElement | null>(null);
    const previousMessageCountRef = useRef<number>(0);
    const [messages, setMessages] = useState<CalculonChatBotMessage[]>([
        {
            role: 'assistant',
            content: 'Hi, I am Calculon, your Calculist AI assistant. Ask me questions about using the site and workflows.',
        },
    ]);
    const [feedbackByMessageIndex, setFeedbackByMessageIndex] = useState<
        Record<number, 'idle' | 'submitting' | 'done' | 'error'>
    >({});
    const [feedbackResultByMessageIndex, setFeedbackResultByMessageIndex] = useState<Record<number, boolean>>({});

    const isPublicRoute = useMemo(() => {
        const path = location.pathname;
        return path.startsWith('/auth/') || path.startsWith('/client/expenses');
    }, [location.pathname]);

    const isProUser = useMemo(() => {
        const subscriptionType = getSubscriptionType(user?.team?.subscriptionType);
        return subscriptionType.toLowerCase() === 'pro';
    }, [user?.team?.subscriptionType]);

    const shouldShowChatBotFab =
        isAuthenticated && calculonChatBotEnabled && !isPublicRoute && !suppressChatFab;
    const canUseChatBot = shouldShowChatBotFab && isProUser;

    useEffect(() => {
        if (suppressChatFab) {
            setIsOpen(false);
        }
    }, [suppressChatFab]);

    useEffect(() => {
        if (!isOpen || !messageListRef.current) {
            return;
        }

        const messageCountIncreased = messages.length > previousMessageCountRef.current;
        previousMessageCountRef.current = messages.length;

        if (!messageCountIncreased || messages.length === 0) {
            return;
        }

        const lastMessageIndex = messages.length - 1;
        const lastMessageElement = messageListRef.current.querySelector<HTMLElement>(`[data-message-index="${lastMessageIndex}"]`);
        if (!lastMessageElement) {
            return;
        }

        const targetTop = Math.max(lastMessageElement.offsetTop - 20, 0);
        messageListRef.current.scrollTo({ top: targetTop, behavior: 'smooth' });
    }, [messages, isOpen]);

    if (!shouldShowChatBotFab) {
        return null;
    }

    const renderMessageContent = (content: string): React.ReactNode => renderTextWithLinks(content, navigate, theme);

    const handleSend = async () => {
        const messageToSend = input.trim();
        if (!messageToSend || isSending) {
            return;
        }

        if (!canUseChatBot) {
            return;
        }

        const nextMessages = [...messages, { role: 'user' as const, content: messageToSend }];
        setMessages(nextMessages);
        setInput('');
        setIsSending(true);

        try {
            const response = await calculonChatBotApiService.ask({
                message: messageToSend,
                conversationHistory: nextMessages
                    .filter((m) => m.role === 'user' || m.role === 'assistant')
                    .slice(-12),
            });

            const assistantMessage = response.message?.trim() || 'I could not generate a response. Please try again.';
            setMessages((prev) => [...prev, { role: 'assistant', content: assistantMessage }]);
        } catch {
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: 'I ran into an error while answering. Please try again in a moment.',
                },
            ]);
        } finally {
            setIsSending(false);
        }
    };

    const handleFeedback = async (messageIndex: number, isHelpful: boolean) => {
        if (!canUseChatBot) {
            return;
        }
        setFeedbackByMessageIndex((prev) => ({ ...prev, [messageIndex]: 'submitting' }));
        const snapshot = messages.slice(Math.max(0, messageIndex - 11), messageIndex + 1);
        try {
            await calculonChatBotApiService.submitFeedback({
                recentMessages: snapshot.map((m) => ({ role: m.role, content: m.content })),
                isHelpful,
            });
            setFeedbackResultByMessageIndex((prev) => ({ ...prev, [messageIndex]: isHelpful }));
            setFeedbackByMessageIndex((prev) => ({ ...prev, [messageIndex]: 'done' }));
        } catch {
            setFeedbackByMessageIndex((prev) => ({ ...prev, [messageIndex]: 'error' }));
        }
    };

    return (
        <>
            {isOpen && (
                <div
                    style={{
                        position: 'fixed',
                        right: theme.spacing.md,
                        // Kept 8px above the FAB button below (button bottom offset + button
                        // height + 8px gap) — see the button's `bottom` for why it's 84px.
                        bottom: `calc(${theme.spacing.md} + 144px)`,
                        width: '340px',
                        maxWidth: 'calc(100vw - 24px)',
                        height: '440px',
                        backgroundColor: theme.colors.surface,
                        border: `1px solid ${theme.colors.border.default}`,
                        borderRadius: theme.borderRadius.lg,
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.text.inverse,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: theme.spacing.sm,
                        }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: theme.spacing.xs }}>
                            <FontAwesomeIcon icon={faRobot} />
                            Calculon Chat
                        </span>
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: theme.colors.text.inverse,
                                cursor: 'pointer',
                                padding: 0,
                            }}
                            aria-label="Close chat"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div
                        ref={messageListRef}
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: theme.spacing.md,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing.sm,
                            backgroundColor: theme.colors.background,
                        }}
                    >
                        {messages.map((message, index) => {
                            const isUserMessage = message.role === 'user';
                            const isAssistantMessage = message.role === 'assistant';
                            const isInitialAssistantMessage = isAssistantMessage && index === 0;
                            const feedbackState = feedbackByMessageIndex[index] ?? 'idle';
                            const feedbackWasHelpful = feedbackResultByMessageIndex[index];
                            return (
                                <div
                                    key={`${message.role}-${index}`}
                                    data-message-index={index}
                                    style={{
                                        alignSelf: isUserMessage ? 'flex-end' : 'flex-start',
                                        maxWidth: '85%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: theme.spacing.xs,
                                    }}
                                >
                                    <div
                                        style={{
                                            backgroundColor: isUserMessage ? theme.colors.primary : theme.colors.surface,
                                            color: isUserMessage ? theme.colors.text.inverse : theme.colors.text.primary,
                                            border: isUserMessage ? 'none' : `1px solid ${theme.colors.border.default}`,
                                            borderRadius: theme.borderRadius.md,
                                            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                            fontSize: theme.typography.fontSize.sm,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {renderMessageContent(message.content)}
                                    </div>
                                    {isAssistantMessage && canUseChatBot && !isInitialAssistantMessage && (
                                        <div
                                            style={{
                                                fontSize: theme.typography.fontSize.xs,
                                                color: theme.colors.text.secondary,
                                            }}
                                        >
                                            {feedbackState === 'done' ? (
                                                <span>
                                                    {feedbackWasHelpful ? FEEDBACK_THANK_YOU_HELPFUL : FEEDBACK_THANK_YOU}
                                                </span>
                                            ) : (
                                                <>
                                                    <span style={{ marginRight: theme.spacing.xs }}>
                                                        Was this helpful?
                                                    </span>
                                                    <CustomTooltip text="Helpful">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            void handleFeedback(index, true);
                                                        }}
                                                        disabled={feedbackState === 'submitting'}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            padding: 0,
                                                            cursor:
                                                                feedbackState === 'submitting' ? 'wait' : 'pointer',
                                                            color: theme.colors.text.secondary,
                                                            fontSize: 'inherit',
                                                            marginRight: theme.spacing.xs,
                                                        }}
                                                        aria-label="Helpful"
                                                    >
                                                        <FontAwesomeIcon icon={faThumbsUp} />
                                                    </button>
                                                    </CustomTooltip>
                                                    <CustomTooltip text="Not helpful">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            void handleFeedback(index, false);
                                                        }}
                                                        disabled={feedbackState === 'submitting'}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            padding: 0,
                                                            cursor:
                                                                feedbackState === 'submitting' ? 'wait' : 'pointer',
                                                            color: theme.colors.text.secondary,
                                                            fontSize: 'inherit',
                                                        }}
                                                        aria-label="Not helpful"
                                                    >
                                                        <FontAwesomeIcon icon={faThumbsDown} />
                                                    </button>
                                                    </CustomTooltip>
                                                    {feedbackState === 'error' && (
                                                        <span style={{ marginLeft: theme.spacing.xs }}>
                                                            Could not submit. Try again.
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {isSending && (
                            <div
                                style={{
                                    alignSelf: 'flex-start',
                                    fontSize: theme.typography.fontSize.sm,
                                    color: theme.colors.text.secondary,
                                }}
                            >
                                Thinking...
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            borderTop: `1px solid ${theme.colors.border.default}`,
                            padding: theme.spacing.sm,
                            display: 'flex',
                            gap: theme.spacing.xs,
                        }}
                    >
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void handleSend();
                                }
                            }}
                            placeholder="Ask a question about the site..."
                            style={{
                                flex: 1,
                                border: `1px solid ${theme.colors.border.default}`,
                                borderRadius: theme.borderRadius.sm,
                                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                fontSize: theme.typography.fontSize.sm,
                                outline: 'none',
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                void handleSend();
                            }}
                            disabled={isSending || !input.trim()}
                            style={{
                                border: 'none',
                                borderRadius: theme.borderRadius.sm,
                                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                backgroundColor: isSending || !input.trim() ? theme.colors.border.default : theme.colors.primary,
                                color: theme.colors.text.inverse,
                                cursor: isSending || !input.trim() ? 'not-allowed' : 'pointer',
                            }}
                            aria-label="Send message"
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                    </div>
                </div>
            )}

            <CustomTooltip text="Open chatbot">
            <button
                type="button"
                onClick={() => {
                    if (!isProUser) {
                        setShowProUpgradeModal(true);
                        return;
                    }
                    setIsOpen((prev) => !prev);
                }}
                style={{
                    position: 'fixed',
                    right: theme.spacing.md,
                    // Raised to clear ManualSaveFab.tsx's Save button (bottom: theme.spacing.lg
                    // = 24px, ~56px tall, so its top edge sits around 80px) by ~20px — do not
                    // move the Save button to fix overlap; adjust this FAB instead.
                    bottom: `calc(${theme.spacing.md} + 84px)`,
                    width: '52px',
                    height: '52px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.inverse,
                    cursor: 'pointer',
                    zIndex: 1000,
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: theme.typography.fontSize.lg,
                }}
                aria-label="Open chatbot"
            >
                <FontAwesomeIcon icon={faRobot} />
            </button>
            </CustomTooltip>
            <ProUpgradeModal
                isOpen={showProUpgradeModal}
                onClose={() => setShowProUpgradeModal(false)}
                title="Pro Feature"
                featureDescription="Calculist chatbot assistance is a Pro-only feature."
                featureKey="CalculonChatBot"
            />
        </>
    );
};
